import './AuthPage.scss';

import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Alert, Button, Select, Spin } from 'antd';
import Session from 'supertokens-auth-react/recipe/session';
import { getUserOrganizationAccess, UserOrganizationAccess } from '../../api/organization';
import { approveOAuthRequest } from '../../api/oauth';
import logoImage from '../../assets/images/logo/logo-full.svg';
import RoutePaths from '../../router/RoutePaths';
import { CheckCircleOutlined } from '@ant-design/icons';
import axios from 'axios';

type Phase = 'checking_session' | 'loading_orgs' | 'consent' | 'submitting' | 'redirecting' | 'error';

export default function OAuthAuthorize() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const authRequestId = searchParams.get('auth_request_id');
  const clientName = searchParams.get('client_name') || 'An application';

  const [phase, setPhase] = useState<Phase>('checking_session');
  const [orgs, setOrgs] = useState<UserOrganizationAccess[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!authRequestId) {
      setErrorMessage('Invalid authorization request. Missing required parameter.');
      setPhase('error');
      return;
    }

    const init = async () => {
      // Check session
      try {
        const hasSession = await Session.doesSessionExist();
        if (!hasSession) {
          navigate(RoutePaths.Login);
          return;
        }
      } catch {
        navigate(RoutePaths.Login);
        return;
      }

      // Load organizations
      setPhase('loading_orgs');
      try {
        const res = await getUserOrganizationAccess();
        const activeOrgs = res.data.filter((org) => org.deletedAt === null);

        if (activeOrgs.length === 0) {
          setErrorMessage('No organizations available. Please create or join an organization first.');
          setPhase('error');
          return;
        }

        setOrgs(activeOrgs);
        setSelectedOrgId(activeOrgs[0].id);
        setPhase('consent');
      } catch {
        setErrorMessage('Failed to load organizations. Please try again.');
        setPhase('error');
      }
    };

    void init();
  }, [authRequestId, navigate]);

  const handleAuthorize = async () => {
    if (!authRequestId || !selectedOrgId) return;

    setPhase('submitting');
    try {
      const res = await approveOAuthRequest({
        auth_request_id: authRequestId,
        organization_id: selectedOrgId,
      });

      setPhase('redirecting');
      window.location.href = res.data.redirect_uri;
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const status = err.response?.status;

        if (status === 401) {
          navigate(RoutePaths.Login);
          return;
        }
        if (status === 403) {
          setErrorMessage('You do not have access to this organization.');
        } else if (status === 400) {
          setErrorMessage('This authorization request has expired. Please retry the connection from your MCP client.');
        } else {
          setErrorMessage('Something went wrong. Please try again.');
        }
      } else {
        setErrorMessage('Something went wrong. Please try again.');
      }
      setPhase('error');
    }
  };

  const handleCancel = () => {
    // window.close() only works for windows opened via window.open()
    if (window.opener) {
      window.close();
    } else {
      navigate(RoutePaths.Home);
    }
  };

  const permissions = ['View and manage tasks', 'View and manage workspaces', 'View and manage comments'];

  if (phase === 'checking_session' || phase === 'loading_orgs') {
    return (
      <div className="auth-page-wrapper">
        <div className="flex flex-col items-center justify-center">
          <img src={logoImage} alt="Taskonio" className="mb-8" />
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (phase === 'submitting' || phase === 'redirecting') {
    return (
      <div className="auth-page-wrapper">
        <div className="flex flex-col items-center justify-center">
          <img src={logoImage} alt="Taskonio" className="mb-8" />
          <Spin size="large" />
          <p className="mt-4 text-sm text-gray-500">{phase === 'submitting' ? 'Authorizing...' : 'Redirecting...'}</p>
        </div>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="auth-page-wrapper">
        <div className="flex flex-col items-center justify-center max-w-md mx-auto">
          <img src={logoImage} alt="Taskonio" className="mb-8" />
          <Alert
            message="Authorization Error"
            description={errorMessage}
            type="error"
            showIcon
            className="w-full mb-6"
          />
          <Button type="default" onClick={handleCancel}>
            Close
          </Button>
        </div>
      </div>
    );
  }

  // consent phase
  return (
    <div className="auth-page-wrapper">
      <div className="flex flex-col items-center justify-center max-w-md mx-auto">
        <img src={logoImage} alt="Taskonio" className="mb-6" />

        <h1 className="text-xl font-semibold mb-2 text-center">{clientName} wants to access your Taskonio account</h1>

        <p className="text-sm text-gray-500 mb-6 text-center">This will allow {clientName} to:</p>

        <div className="w-full bg-gray-50 rounded-lg p-4 mb-6">
          {permissions.map((perm) => (
            <div key={perm} className="flex items-center gap-2 py-1.5">
              <CheckCircleOutlined className="text-green-500" />
              <span className="text-sm">{perm}</span>
            </div>
          ))}
        </div>

        {orgs.length > 1 ? (
          <div className="w-full mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Organization</label>
            <Select
              className="w-full"
              size="large"
              value={selectedOrgId}
              onChange={(value) => setSelectedOrgId(value)}
              options={orgs.map((org) => ({
                value: org.id,
                label: org.name,
              }))}
            />
          </div>
        ) : (
          <div className="w-full mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Organization</label>
            <p className="text-sm text-brand-grey-dark bg-gray-50 rounded-lg px-3 py-2.5">{orgs[0]?.name}</p>
          </div>
        )}

        <div className="w-full flex flex-col gap-3">
          <Button
            type="primary"
            size="large"
            shape="round"
            block
            className="auth-submit-button font-semibold"
            onClick={handleAuthorize}
          >
            Authorize
          </Button>
          <Button type="default" size="large" shape="round" block onClick={handleCancel}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
